"use client";

import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslations } from "next-intl";
import { ProfileOutlined } from "@ant-design/icons";
import BlogSearch from "@/components/Blogs/BlogSeach.tsx";
import { Blog } from "@/lib/@types/api/blog";
import { Link } from "@/navigation.ts";
import FeatureBlogs from "./FeaturedBlogs";
import { useLocale } from "next-intl";

// 使用 TailwindCSS 的一些自定义样式来优化文章显示
export const StyledArticle = styled.article`
	/* 定义与全局无关的样式 */
	p {
		display: block;
		margin-block-start: 1em;
		margin-block-end: 1em;
		margin-inline-start: 0px;
		margin-inline-end: 0px;
		unicode-bidi: isolate;
	}

	h1 {
		color: #18181b;
		font-weight: 700 !important;
		font-size: 28px;
		padding: 4px 0;
	}

	h1 span {
		color: inherit;
		font-weight: inherit;
		font-size: inherit;
		padding: inherit;
	}

	/* 列表样式 */
	ul {
		list-style-type: disc;
		margin-left: 20px;
		padding-left: 0;
	}

	li {
		margin-bottom: 10px;
	}

	strong {
		padding: 0 2px;
	}

	/* 标题样式 */
	h2 {
		color: #18181b;
		font-weight: 700 !important;
		font-size: 24px;
		padding: 4px 0;
	}

	h2 span {
		color: inherit;
		font-weight: inherit;
		font-size: inherit;
		padding: inherit;
	}

	h3 {
		color: #18181b;
		font-weight: 700 !important;
		font-size: 20px;
		padding: 4px 0;
	}

	h3 span {
		color: inherit;
		font-weight: inherit;
		font-size: inherit;
		padding: inherit;
	}

	h4 {
		color: #18181b;
		font-weight: 600 !important;
		font-size: 16px;
		padding: 4px 0;
	}

	h4 span {
		color: inherit;
		font-weight: inherit;
		font-size: inherit;
		padding: inherit;
	}

	h5 {
		color: #18181b;
		font-weight: 600 !important;
		font-size: 14px;
		padding: 4px 0;
	}

	h5 span {
		color: inherit;
		font-weight: inherit;
		font-size: inherit;
		padding: inherit;
	}

	h6 {
		color: #18181b;
		font-weight: 500 !important;
		font-size: 13px;
		padding: 4px 0;
	}

	h6 span {
		color: inherit;
		font-weight: inherit;
		font-size: inherit;
		padding: inherit;
	}

	span {
		color: #18181b;
		font-weight: 400;
		font-size: 16px;
		padding: 4px 0;
	}

	/* 表格样式 */
	table {
		width: 100%;
		border-collapse: collapse;
		margin-top: 1em;
		margin-bottom: 1em;
	}

	th,
	td {
		border: 1px solid #dcdcdc;
		padding: 8px;
		text-align: left;
	}

	/* 表格内文字 */
	table p {
		margin: 0;
	}
	a {
		padding: 0 2px;
	}
	img {
		display: inline-block;
		white-space: nowrap;
	}

	/* 添加其他必要的样式，确保不被全局样式覆盖 */
`;

const BlogArticle = ({ content, blogList }: { content: string; blogList: Blog.BlogListItem[] }) => {
	// 修改状态以存储带有层级信息的标题
	const [headings, setHeadings] = useState<{ text: string; level: number; id: string }[]>([]);
	const [newContent, setNewContent] = useState<string>(content); // 用于存储修改后的内容

	// 提取文章的 H1-H5 标题并生成目录，同时更新 newContent
	useEffect(() => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(content, "text/html");

		// 提取标题并生成 ID
		const foundHeadings = Array.from(doc.querySelectorAll("h1, h2, h3, h4, h5")).map((h, index) => {
			const headingText = h.textContent || "";
			const headingId = headingText.trim().toLowerCase().replace(/\s+/g, "-"); // 将空格转义为 "-"
			h.id = headingId; // 给每个标题元素设置 ID

			// 获取标题级别
			const level = parseInt(h.tagName.substring(1));

			return {
				text: headingText,
				level: level,
				id: headingId,
			};
		});

		// 更新内容为带有 ID 的 HTML
		const updatedContent = new XMLSerializer().serializeToString(doc); // 将修改后的文档序列化为 HTML
		setNewContent(updatedContent); // 更新 newContent
		setHeadings(foundHeadings); // 更新目录
	}, [content]);

	const t = useTranslations();
	let locale = useLocale();

	// 函数用于计算缩进空间
	const getIndentClass = (level: number) => {
		// 根据标题级别设置缩进
		switch (level) {
			case 1:
				return "ml-0";
			case 2:
				return "ml-3";
			case 3:
				return "ml-6";
			case 4:
				return "ml-9";
			case 5:
				return "ml-12";
			default:
				return "ml-0";
		}
	};

	return (
		<div className="relative flex justify-between !gap-5 py-8  max-md:flex-wrap ">
			{/* 左侧文章内容 */}
			<StyledArticle
				className="animate__animated  animate__fadeIn w-full max-w-[1200px] flex-1 overflow-auto overflow-y-auto  max-2xl:max-w-[900px]"
				style={{
					scrollbarWidth: "none", // Firefox
					msOverflowStyle: "none", // IE 10+
				}}
			>
				{/* 渲染修改后的内容，并通过 dangerouslySetInnerHTML 来处理 */}
				<div
					className="blog-content w-full"
					dangerouslySetInnerHTML={{
						__html: newContent, // 渲染修改后的内容
					}}
				/>
			</StyledArticle>
			{/*{isFixed&&<div className="w-[450px] "></div>}*/}

			{/* 右侧 Table of Contents (TOC) */}
			{/*<div className={` ${isFixed ? ' fixed top-[105px]  z-50 container w-full  flex justify-end' : ''}`}>*/}
			<div className={`absolute right-0 w-[450px] max-md:hidden`}>
				<nav className={` border-gray-450 my-4 h-fit rounded border bg-[#f9f9f9] p-4 `}>
					<div className="mb-4 flex items-center justify-between">
						<h3 className="text-xl font-semibold ">{t("blog.Contents")}</h3>
						<ProfileOutlined className="rounded-xl text-2xl text-gray-300" />
					</div>
					<ul className="space-y-2">
						{headings
							.filter((heading) => heading.level === 2) // 只筛选h2标题
							.map((heading, index) => (
								<li key={index} className={`cursor-pointer text-[36px] hover:underline`}>
									<span className="mr-1">•</span>
									<a className="text-gray-700" href={`#${heading.id}`}>
										{heading.text}
									</a>
								</li>
							))}
					</ul>
				</nav>
				<BlogSearch></BlogSearch>
				<FeatureBlogs locale={locale}></FeatureBlogs>
			</div>

			{/*</div>*/}
		</div>
	);
};
const RelatedBlog = ({ blogList }: { blogList: Blog.BlogListItem[] }) => {
	const t = useTranslations();

	return (
		<>
			<h3 className={"my-4 border-b border-b-gray-400 py-4 text-xl"}>{t("blog.RelatedBlogs")}</h3>
			<ul>
				{blogList &&
					blogList.map((blog) => {
						return (
							<li key={blog.blog_id} className="border-b border-b-gray-400">
								<Link href={"/blog/" + blog.blog_slug}>
									<h3 className="my-2  line-clamp-2 !text-xl font-bold  text-black ">{blog.blog_title}</h3>
									{blog.blog_classification_list &&
										blog.blog_classification_list.map((item, index) => {
											return (
												index === 0 && (
													<div className=" py-4 text-xs  uppercase text-main" key={item.cls_slug}>
														{item.cls_name}
													</div>
												)
											);
										})}{" "}
								</Link>
							</li>
						);
					})}
			</ul>
		</>
	);
};

export default BlogArticle;
